// Utility function to get user data from either localStorage or sessionStorage
// This handles the "Remember Me" functionality where data can be in either storage

import { enhancedTokenManager } from './enhancedTokenManager';

/**
 * Get user data from appropriate storage (localStorage or sessionStorage)
 * This function should be used instead of directly accessing localStorage
 * @returns {Object|null} User data object or null if not found
 */
export const getUserData = () => {
  try {
    // Use the enhanced token manager which knows where the data is stored
    return enhancedTokenManager.getUserData();
  } catch (error) {
    return null;
  }
};

/**
 * Get a specific property from user data
 * @param {string} property - The property to retrieve (e.g., 'buildingId', 'userId')
 * @returns {any} The property value or undefined if not found
 */
export const getUserProperty = (property) => {
  const userData = getUserData();
  return userData?.[property];
};

/**
 * Update user data in the appropriate storage
 * @param {Object} updates - Object with properties to update
 * @returns {boolean} True if successful, false otherwise
 */
export const updateUserData = (updates) => {
  try {
    const currentData = getUserData();
    if (!currentData) return false;
    
    const updatedData = { ...currentData, ...updates };
    const rememberMe = enhancedTokenManager.isRememberMeEnabled();
    enhancedTokenManager.setUserData(updatedData, rememberMe);
    
    // Dispatch event for components to listen to
    window.dispatchEvent(new CustomEvent('userDataUpdated', {
      detail: { userData: updatedData }
    }));
    
    return true;
  } catch (error) {
    return false;
  }
};

// For backward compatibility - components can still use this pattern
export const getStoredUser = () => {
  const userData = getUserData();
  return userData ? JSON.stringify(userData) : null;
};